import { createTheme } from '@mui/material/styles';

const normalizeHexColor = (hexColor, fallback = '#977F6B') => {
  if (!hexColor || typeof hexColor !== 'string') return fallback;
  const normalized = hexColor.trim().replace('#', '');
  if (/^[0-9A-Fa-f]{6}$/.test(normalized)) return `#${normalized.toUpperCase()}`;
  if (/^[0-9A-Fa-f]{3}$/.test(normalized)) {
    const expanded = normalized.split('').map((char) => `${char}${char}`).join('');
    return `#${expanded.toUpperCase()}`;
  }
  return fallback;
};

const hexToRgb = (hexColor) => {
  const safe = normalizeHexColor(hexColor).replace('#', '');
  return {
    r: parseInt(safe.slice(0, 2), 16),
    g: parseInt(safe.slice(2, 4), 16),
    b: parseInt(safe.slice(4, 6), 16),
  };
};

const hexToRgba = (hexColor, alpha = 1) => {
  const { r, g, b } = hexToRgb(hexColor);
  const safeAlpha = Math.max(0, Math.min(1, alpha));
  return `rgba(${r}, ${g}, ${b}, ${safeAlpha})`;
};

const isVeryLightColor = (hexColor) => {
  const { r, g, b } = hexToRgb(hexColor);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance >= 0.82;
};

const darkenHex = (hexColor, factor = 0.55) => {
  const { r, g, b } = hexToRgb(hexColor);
  const toHex = (value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0').toUpperCase();
  return `#${toHex(r * factor)}${toHex(g * factor)}${toHex(b * factor)}`;
};

export const createAppTheme = (primaryColor = '#977F6B', secondaryColor = '#5E564F') => {
  const resolvedPrimary = normalizeHexColor(primaryColor, '#977F6B');
  const resolvedSecondary = normalizeHexColor(secondaryColor, '#5E564F');
  const effectivePrimary = isVeryLightColor(resolvedPrimary) ? darkenHex(resolvedPrimary, 0.45) : resolvedPrimary;

  // Important: les couleurs issues du logo doivent impacter l'accent (primary/secondary)
  // mais ne doivent jamais teinter la couleur de texte globale de l'interface.
  const baseTextPrimary = '#5E564F';
  const baseTextSecondary = '#6E645D';
  const baseTextDisabled = '#A79D95';

  const pageBackground = `linear-gradient(145deg, ${hexToRgba(effectivePrimary, 0.34)} 0%, ${hexToRgba(effectivePrimary, 0.18)} 42%, ${hexToRgba(resolvedSecondary, 0.26)} 100%)`;

  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: effectivePrimary,
        light: '#B49F8C',
        dark: '#6F5C4D',
        contrastText: '#ffffff',
      },
      secondary: {
        main: resolvedSecondary,
        light: '#7B7168',
        dark: '#4A433D',
        contrastText: '#ffffff',
      },
      background: {
        default: pageBackground,
        paper: '#FAF6F2',
        gradient: `linear-gradient(135deg, ${effectivePrimary} 0%, #8A7563 52%, ${resolvedSecondary} 100%)`,
        darkGradient: `linear-gradient(135deg, ${resolvedSecondary} 0%, #786B61 55%, ${effectivePrimary} 100%)`,
      },
      text: {
        primary: baseTextPrimary,
        secondary: baseTextSecondary,
        disabled: baseTextDisabled,
      },
      grey: {
        50: '#f8f5f3',
        100: '#efe8e2',
        200: '#e1d7ce',
        300: '#cdbeb1',
        400: '#b7a393',
        500: '#9d8878',
        600: '#7e6d61',
        700: '#63564d',
        800: '#4b413a',
        900: '#312a26',
      },
      neon: {
        primary: '0px 0px 24px rgba(151, 127, 107, 0.4)',
        secondary: '0px 0px 24px rgba(94, 86, 79, 0.38)',
        glow: '0px 0px 34px rgba(151, 127, 107, 0.55)',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      button: {
        fontSize: '1rem',
        fontWeight: 600,
      },
      h1: {
        fontWeight: 700,
        fontSize: '3.1rem',
        color: baseTextPrimary,
        textShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
      },
      h2: {
        fontWeight: 600,
        fontSize: '2.6rem',
        color: baseTextPrimary,
        textShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
      },
      h3: {
        fontWeight: 600,
        fontSize: '2.1rem',
        color: baseTextPrimary,
        textShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.85rem',
        color: baseTextPrimary,
        textShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
      },
      h5: {
        fontWeight: 500,
        fontSize: '1.6rem',
        color: baseTextPrimary,
      },
      h6: {
        fontWeight: 500,
        fontSize: '1.3rem',
        color: baseTextPrimary,
      },
      body1: {
        fontSize: '1.12rem',
        lineHeight: 1.6,
        color: '#5E564F',
      },
      body2: {
        fontSize: '1.02rem',
        lineHeight: 1.5,
        color: '#6E645D',
      },
      caption: {
        fontSize: '0.92rem',
        lineHeight: 1.4,
        color: '#6E645D',
      },
    },
    shape: {
      borderRadius: 16,
    },
    shadows: [
      'none',
      '0px 2px 10px rgba(94, 86, 79, 0.08)',
      '0px 5px 16px rgba(94, 86, 79, 0.12)',
      '0px 10px 28px rgba(94, 86, 79, 0.16)',
      '0px 14px 36px rgba(94, 86, 79, 0.2)',
      '0px 0px 22px rgba(151, 127, 107, 0.28)',
      '0px 0px 30px rgba(151, 127, 107, 0.4)',
      '0px 0px 40px rgba(151, 127, 107, 0.5)',
      '0px 4px 20px rgba(94, 86, 79, 0.2), 0px 0px 22px rgba(151, 127, 107, 0.25)',
      '0px 9px 34px rgba(94, 86, 79, 0.22), 0px 0px 30px rgba(151, 127, 107, 0.35)',
      ...Array(15).fill('0px 9px 34px rgba(94, 86, 79, 0.2)'),
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: pageBackground,
            minHeight: '100vh',
            fontSmoothing: 'antialiased',
          },
          'input:-webkit-autofill, textarea:-webkit-autofill, select:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px rgba(255, 255, 255, 0.8) inset',
            boxShadow: '0 0 0 1000px rgba(255, 255, 255, 0.8) inset',
            WebkitTextFillColor: baseTextPrimary,
            caretColor: baseTextPrimary,
            transition: 'background-color 9999s ease-out 0s',
          },
          'input:-webkit-autofill:focus, textarea:-webkit-autofill:focus, select:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 1000px rgba(255, 255, 255, 0.8) inset',
            boxShadow: '0 0 0 1000px rgba(255, 255, 255, 0.8) inset',
            WebkitTextFillColor: baseTextPrimary,
            caretColor: baseTextPrimary,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            background: 'linear-gradient(135deg, #FAF6F2 0%, #EAE3DC 100%)',
            backdropFilter: 'blur(20px)',
            borderRadius: 20,
            border: '1px solid rgba(107, 98, 91, 0.2)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0px 14px 44px rgba(94, 86, 79, 0.2), 0px 0px 24px rgba(197, 164, 109, 0.16)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            padding: '10px 24px',
            transition: 'all 0.3s ease-in-out',
          },
          contained: {
            background: `linear-gradient(135deg, ${effectivePrimary} 0%, #8A7563 52%, ${resolvedSecondary} 100%)`,
            boxShadow: '0px 6px 18px rgba(94, 86, 79, 0.24)',
            '&:hover': {
              background: `linear-gradient(135deg, ${resolvedSecondary} 0%, #7A695A 52%, ${effectivePrimary} 100%)`,
              boxShadow: '0px 10px 28px rgba(94, 86, 79, 0.32), 0px 0px 22px rgba(151, 127, 107, 0.2)',
              transform: 'translateY(-2px)',
            },
          },
          outlined: {
            borderColor: resolvedSecondary,
            color: resolvedSecondary,
            borderWidth: 2,
            '&:hover': {
              borderColor: resolvedSecondary,
              backgroundColor: 'rgba(151, 127, 107, 0.08)',
              borderWidth: 2,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease-in-out',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: effectivePrimary,
                boxShadow: '0px 0px 15px rgba(151, 127, 107, 0.25)',
              },
              '&.Mui-focused': {
                boxShadow: '0px 0px 22px rgba(151, 127, 107, 0.3)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: effectivePrimary,
                  borderWidth: 2,
                },
              },
            },
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            background: 'linear-gradient(135deg, #FDFBF8 0%, #F2ECE5 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(107, 98, 91, 0.18)',
            overflow: 'hidden',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            background: `linear-gradient(135deg, ${effectivePrimary} 0%, ${resolvedSecondary} 100%)`,
            '& .MuiTableCell-head': {
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '1rem',
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(151, 127, 107, 0.08)',
              transform: 'scale(1.01)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            fontWeight: 500,
          },
          colorPrimary: {
            background: `linear-gradient(135deg, ${effectivePrimary} 0%, ${effectivePrimary} 100%)`,
            color: '#ffffff',
          },
          colorSuccess: {
            background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
            color: '#ffffff',
          },
          colorError: {
            background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
            color: '#ffffff',
          },
          colorWarning: {
            background: `linear-gradient(135deg, ${effectivePrimary} 0%, #8A7563 100%)`,
            color: '#ffffff',
          },
          colorInfo: {
            background: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',
            color: '#ffffff',
          },
        },
      },
    },
  });
};

const theme = createAppTheme();

export default theme;